import { useEffect, useState } from "react";
import { Bell, BellOff, Trash2, Send } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useFlash } from "../lib/flash";
import { relTime } from "../lib/format";
import { pushState, currentSubscription, enablePush, disablePush } from "../lib/push";

const TYPES = [["order", "Nye ja tak"], ["waitlist", "Venteliste og afbud"], ["fb", "Facebook mistede forbindelsen"], ["billing", "Faktura/abonnement"]];

export function StateHint({ state }) {
  if (state === "ios-hint") return <p className="ps-hint" data-testid="push-ios-hint">Virker på iPhone, når JaTak Pro er føjet til hjemmeskærmen (iOS 16.4+). Åbn appen fra hjemmeskærmen og slå det til her.</p>;
  if (state === "unsupported") return <p className="ps-hint" data-testid="push-unsupported">Denne browser understøtter ikke push-beskeder. Prøv Chrome, Edge, Firefox eller Safari – eller installer appen på telefonen.</p>;
  if (state === "denied") return <p className="ps-hint warn" data-testid="push-denied">Beskeder er blokeret for jatakpro.dk. Tryk på hængelåsen eller ⓘ ved adressen → Tilladelser → Notifikationer → Tillad. Genindlæs derefter siden.</p>;
  return null;
}

export default function PushSettings() {
  const { store } = useAuth();
  const flash = useFlash();
  const [d, setD] = useState(null);
  const [state, setState] = useState(pushState);
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/push/subscriptions").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => {
    load();
    currentSubscription().then((s) => setOn(!!s)).catch(() => {});
  }, []);
  if (!d || store?.staff) return null;

  const toggle = async () => {
    setBusy(true);
    try {
      if (on) { await disablePush(); setOn(false); flash("Push er slået fra på denne enhed."); }
      else { await enablePush(); setOn(true); flash("Push er slået til på denne enhed."); }
    } catch (e) {
      if (e.code === "denied") setState("denied");
      else flash(apiError(e.response?.data?.detail) || "Kunne ikke slå push til", "error");
    } finally { setBusy(false); load(); }
  };
  const setPref = async (k, val) => {
    const prefs = { ...d.prefs, [k]: val };
    setD({ ...d, prefs });
    await api.put("/push/prefs", prefs).catch(() => flash("Kunne ikke gemme", "error"));
  };
  const test = async () => {
    try { const r = await api.post("/push/test"); flash(r.data.flash); }
    catch (e) { flash(apiError(e.response?.data?.detail) || "Kunne ikke sende", "error"); }
    load();
  };

  return (
    <section className="panel" id="push" data-testid="push-settings">
      <h2><Bell size={18} /> Push-beskeder på denne enhed</h2>
      <p className="muted">Få besked med det samme, når kunderne siger ja tak – også når appen er lukket.</p>
      {state === "ok" ? (
        <label className="ps-toggle">
          <input type="checkbox" checked={on} disabled={busy} onChange={toggle} data-testid="push-toggle" />
          <span>{on ? <Bell size={16} /> : <BellOff size={16} />} Få besked på denne enhed</span>
        </label>
      ) : <StateHint state={state} />}

      <h3 className="ps-h">Beskeder om</h3>
      <div className="ps-types">
        {TYPES.map(([k, l]) => (
          <label key={k} className="ps-check">
            <input type="checkbox" checked={!!d.prefs[k]} onChange={(e) => setPref(k, e.target.checked)} data-testid={`push-pref-${k}`} /> {l}
          </label>
        ))}
        <label className="ps-check">
          <input type="checkbox" checked={!!d.prefs.quiet} onChange={(e) => setPref("quiet", e.target.checked)} data-testid="push-pref-quiet" /> Ingen beskeder mellem 22 og 07
        </label>
      </div>

      <h3 className="ps-h">Enheder med push ({d.items.length})</h3>
      <ul className="ps-devices" data-testid="push-devices">
        {d.items.map((s) => (
          <li key={s.id} data-testid={`push-device-${s.id}`}>
            <span><b>{s.label}</b><small>{s.last_success_at ? `sidste besked ${relTime(s.last_success_at)}` : `tilføjet ${relTime(s.created_at)}`}</small></span>
            <button type="button" className="btn small" onClick={async () => { await api.delete(`/push/subscriptions/${s.id}`); const cur = await currentSubscription(); if (cur && cur.endpoint.endsWith(s.endpoint_hint)) { await cur.unsubscribe(); setOn(false); } load(); }}
              data-testid={`push-device-remove-${s.id}`}><Trash2 size={14} /> Fjern</button>
          </li>
        ))}
        {!d.items.length && <li className="muted" data-testid="push-devices-empty">Ingen enheder endnu.</li>}
      </ul>
      <button type="button" className="btn" onClick={test} disabled={!d.items.length} data-testid="push-test"><Send size={15} /> Send testbesked</button>
    </section>
  );
}
